import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import { ListTree, Plus, Search, Table2 } from "lucide-react";
import type { TenantAttributeSpec } from "@the-real-insight/tri-model";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Row, classKey, type RowCtx } from "../../../administration/din276/Din276TreeRow";
import type { DinAttr, DinNode, DinTree } from "../../../administration/din276/din276Tree";
import AttributeDialog from "./AttributeDialog";
import type { CreateResult } from "./dataModelAPI";
import {
  EMPTY_FILTER,
  type ClassFilter,
  type TenantAttribute,
  attributesByClass,
  isFilterActive,
  isOutsideTree,
  matchClasses,
  toDinAttr,
  visibleInTree,
} from "./dataModel";

/**
 * "Klassen": the DIN 276 classes, filtered by name (and comments) and code, as a table or as the
 * tree of the administration. An attribute can be added to every class of the filter result at once.
 */

interface Properties {
  tree: DinTree;
  attributes: TenantAttribute[];
  canWrite: boolean;
  onCreate: (classCodes: string[], spec: TenantAttributeSpec) => Promise<CreateResult>;
}

export default function ClassesTab({ tree, attributes, canWrite, onCreate }: Properties) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "de";
  const fieldId = useId();

  const [filter, setFilter] = useState<ClassFilter>(EMPTY_FILTER);
  const [view, setView] = useState<"table" | "tree">("table");
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [whereOpen, setWhereOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const hits = useMemo(() => matchClasses(tree, filter), [tree, filter]);
  const own = useMemo(() => attributesByClass(attributes), [attributes]);
  const filtered = isFilterActive(filter);

  const change = (patch: Partial<ClassFilter>) => {
    setFilter((current) => ({ ...current, ...patch }));
    setPage(1);
  };

  const toggle = useCallback(
    (key: string) =>
      setExpanded((current) => {
        const next = new Set(current);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    [],
  );

  const create = async (spec: TenantAttributeSpec) => {
    const result = await onCreate(
      hits.map((node) => node.code),
      spec,
    );
    setAdding(false);

    const skipped = result.skipped.length;
    const message = t("operatorObligationsPanel.dataModel.classes.added", {
      name: spec.name,
      count: result.created.length,
    });
    if (skipped === 0) {
      toast.success(message);
    } else {
      const builtin = result.skipped.filter((entry) => entry.reason === "builtin").length;
      const duplicate = result.skipped.filter((entry) => entry.reason === "duplicate").length;
      toast.warning(message, {
        description: t("operatorObligationsPanel.dataModel.classes.skipped", {
          count: skipped,
          builtin,
          duplicate,
        }),
      });
    }
  };

  const columns: ColumnDef<DinNode>[] = [
    { id: "code", header: t("operatorObligationsPanel.dataModel.classes.code"), accessorFn: (node) => node.code, size: 110 },
    { id: "label", header: t("operatorObligationsPanel.dataModel.classes.name"), accessorFn: (node) => node.label, size: 320 },
    {
      id: "din",
      header: t("operatorObligationsPanel.dataModel.classes.dinAttributes"),
      accessorFn: (node) => node.attrs.length,
      size: 120,
    },
    {
      id: "own",
      header: t("operatorObligationsPanel.dataModel.classes.ownAttributes"),
      accessorFn: (node) => own.get(node.code)?.length ?? 0,
      size: 120,
      cell: ({ row }) => {
        const count = own.get(row.original.code)?.length ?? 0;
        return <span className={cn(count > 0 && "font-semibold text-primary")}>{count}</span>;
      },
    },
  ];

  // The hits and the path to them. Without a text or code filter the hits are every class the two
  // switches let through, so the tree still hides what the administration hides by default.
  const visible = useMemo(() => visibleInTree(tree, hits), [tree, hits]);
  const hitCodes = useMemo(() => new Set(hits.map((node) => node.code)), [hits]);

  // A new filter opens the path down to every hit; the hits themselves stay folded, so the tree
  // reads as a list of places, and a click opens what is there.
  useEffect(() => {
    if (!filtered) {
      setExpanded(new Set());
      return;
    }
    setExpanded(
      new Set(
        Array.from(visible)
          .filter((code) => {
            const node = tree.byCode[code];
            // Expand if: not a hit, OR has visible children (is ancestor of another hit)
            return !hitCodes.has(code) || node?.children.some((child) => visible.has(child));
          })
          .map(classKey),
      ),
    );
  }, [filtered, visible, hitCodes, tree]);

  const ownDinAttrs = useCallback(
    (code: string): DinAttr[] => (own.get(code) ?? []).map((attribute) => toDinAttr(attribute, lang)),
    [own, lang],
  );

  /**
   * The context the rows of the administration tree read — the same rows, so a class looks here
   * exactly as it does there, plus the customer's attributes in its datasheet.
   */
  const rowContext: RowCtx = {
    tree,
    expanded,
    toggle,
    whereOpen,
    setWhereOpen,
    // "Wo verwendet" jumps to the owning class: open the path to it.
    revealParent: (owner) => {
      const keys: string[] = [];
      for (let code: string | undefined = owner; code; code = tree.parentOf[code]) keys.push(classKey(code));
      setExpanded((current) => new Set([...Array.from(current), ...keys]));
    },
    flash: null,
    matchSet: filtered ? hitCodes : new Set(),
    isHidden: (code) => !visible.has(code),
    ownAttrs: ownDinAttrs,
    ownLabel: t("operatorObligationsPanel.dataModel.own"),
  };

  const treeRoots = tree.roots.filter((code) => visible.has(code));
  // The K. / T. / F. types have no level and so no place in the classification tree; a hit among
  // them stands below it, on its own.
  const outside = hits.filter(isOutsideTree);

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex min-w-0 flex-wrap items-end gap-4">
        <div className="flex w-[300px] max-w-full flex-col gap-1.5">
          <Label htmlFor={`${fieldId}-text`}>{t("operatorObligationsPanel.dataModel.classes.textFilter")}</Label>
          <div className="relative">
            <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id={`${fieldId}-text`}
              type="search"
              className="pl-9"
              placeholder={t("operatorObligationsPanel.dataModel.classes.textPlaceholder")}
              value={filter.text}
              onChange={(event) => change({ text: event.target.value })}
            />
          </div>
        </div>
        <div className="flex w-[160px] max-w-full flex-col gap-1.5">
          <Label htmlFor={`${fieldId}-code`}>{t("operatorObligationsPanel.dataModel.classes.codeFilter")}</Label>
          <Input
            id={`${fieldId}-code`}
            type="search"
            placeholder="440"
            value={filter.code}
            onChange={(event) => change({ code: event.target.value })}
          />
        </div>
        <label className="flex items-center gap-2 pb-2.5 text-sm">
          <Checkbox checked={filter.showSynthetic} onCheckedChange={(checked) => change({ showSynthetic: checked === true })} />
          {t("operatorObligationsPanel.dataModel.classes.showSynthetic")}
        </label>
        <label className="flex items-center gap-2 pb-2.5 text-sm">
          <Checkbox checked={filter.showPending} onCheckedChange={(checked) => change({ showPending: checked === true })} />
          {t("operatorObligationsPanel.dataModel.classes.showPending")}
        </label>
      </div>

      <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-md border p-0.5" role="group" aria-label={t("operatorObligationsPanel.dataModel.classes.view")}>
            <Button size="sm" variant={view === "table" ? "secondary" : "ghost"} className="gap-1.5" onClick={() => setView("table")}>
              <Table2 size={14} aria-hidden />
              {t("operatorObligationsPanel.dataModel.classes.table")}
            </Button>
            <Button size="sm" variant={view === "tree" ? "secondary" : "ghost"} className="gap-1.5" onClick={() => setView("tree")}>
              <ListTree size={14} aria-hidden />
              {t("operatorObligationsPanel.dataModel.classes.tree")}
            </Button>
          </div>
          <span className="text-sm text-muted-foreground">
            {t("operatorObligationsPanel.dataModel.classes.count", { count: hits.length })}
          </span>
        </div>

        {canWrite ? (
          <Button
            size="sm"
            className="gap-1.5"
            disabled={!filtered || hits.length === 0}
            title={!filtered ? t("operatorObligationsPanel.dataModel.classes.filterFirst") : undefined}
            onClick={() => setAdding(true)}
          >
            <Plus size={14} aria-hidden />
            {t("operatorObligationsPanel.dataModel.classes.add", { count: hits.length })}
          </Button>
        ) : null}
      </div>

      {view === "table" ? (
        <div className="min-w-0 overflow-x-auto">
          <DataTable
            columns={columns}
            data={hits}
            pageSize={15}
            page={page}
            onPageChange={setPage}
            emptyMessage={t("operatorObligationsPanel.dataModel.classes.none")}
            previousAriaLabel={t("operatorResponsibilityPanel.previousPage")}
            nextAriaLabel={t("operatorResponsibilityPanel.nextPage")}
          />
        </div>
      ) : (
        <div className="din276tree max-h-[640px] min-w-0 overflow-auto rounded-md border p-2">
          {hits.length === 0 ? (
            <p className="p-4 text-center text-sm text-muted-foreground">{t("operatorObligationsPanel.dataModel.classes.none")}</p>
          ) : (
            <TooltipProvider delayDuration={200}>
              {[...treeRoots, ...outside.map((node) => node.code)].map((code) => (
                <Row key={code} ctx={rowContext} code={code} depth={0} nodeKey={classKey(code)} isPart={false} seen={new Set()} />
              ))}
            </TooltipProvider>
          )}
        </div>
      )}

      <AttributeDialog
        open={adding}
        title={t("operatorObligationsPanel.dataModel.classes.addTitle")}
        description={t("operatorObligationsPanel.dataModel.classes.addDescription", { count: hits.length })}
        confirmLabel={t("operatorObligationsPanel.dataModel.classes.addConfirm")}
        onDismiss={() => setAdding(false)}
        onConfirm={create}
      />
    </div>
  );
}
